import * as React from "react";
import { useEffect, useState } from "react";
import {
  gameOverview,
  fetchGameOverview,
  gameApiSliceActions,
  gameData,
} from "../../state/game/game-api-slice";
import { useAppDispatch, useAppSelector } from "../../state/hooks";
import WDCountdownPill from "./WDCountdownPill";
import WDPillScroller from "./WDPillScroller";
import { abbrMap } from "../../enums/Country";
import Season from "../../enums/Season";
import { IOrderDataHistorical } from "../../models/Interfaces";
import { store } from "../../state/store";
import t from "../../i18n";

const getCurPhaseMinutes = function (phaseMinutes, phaseMinutesRB, phase) {
  if (phaseMinutesRB !== -1 && (phase === "Retreats" || phase === "Builds")) {
    return phaseMinutesRB;
  }
  return phaseMinutes;
};

/*
 * 人机局「过回合」节奏：命令就绪后先倒计时，到点后主动刷新棋盘数据。
 *
 * 为什么需要它：人机局不靠 SSE 推送（且推送可能不通），如果只等服务器推送，
 * 玩家会停在原地、必须手动刷新才看到新回合。这里由前端自己倒计时并轮询，
 * 直到真的进入下一回合（user 的 Ready 状态被清除）。
 */
const BOT_RESOLVE_SECONDS = 6;
const BOT_POLL_INTERVAL_MS = 1500;
// 结算超过这么久还没推进，就给个明确提示（例如旧局策略卡死）
const BOT_RESOLVE_TIMEOUT_MS = 45000;

interface WDPhaseUIProps {
  gamePhase: string;
  gameSeason: Season;
  gameYear: number;
  viewedPhase: string;
  viewedSeason: Season;
  viewedYear: number;
  orders: IOrderDataHistorical[];
  phaseSelectorOpen: boolean;
}

const WDPhaseUI: React.FC<WDPhaseUIProps> = function ({
  gamePhase,
  gameSeason,
  gameYear,
  viewedPhase,
  viewedSeason,
  viewedYear,
  orders,
  phaseSelectorOpen,
}: WDPhaseUIProps): React.ReactElement {
  const {
    phaseMinutes,
    phaseMinutesRB,
    processTime,
    phase,
    processStatus,
    pauseTimeRemaining,
    playerTypes,
    gameID,
    user,
  } = useAppSelector(gameOverview);

  const dispatch = useAppDispatch();

  const phaseSeconds =
    getCurPhaseMinutes(phaseMinutes, phaseMinutesRB, phase) * 60;

  const gameIsFinished = gamePhase === "Finished";
  const seconds = Math.floor(new Date().getTime() / 1000);

  // 人机局的阶段时长是「无限」（只在真人就绪后推进），显示倒计时只会
  // 显示成一个巨大的数字，没有任何意义，所以这里直接不显示计时条。
  const isBotGame = playerTypes === "MemberVsBots";
  const gameDataResponse = useAppSelector(gameData);
  /*
   * 沙盒建造阶段的特殊情况：界面上的 member 只是其中一个国家，它可能
   * 没有建造名额（None），但别的国家还有没填的建造槽。此时服务端还不会
   * 结算，前端不能直接进入「自动结算」，否则就是白等到超时。
   */
  const pendingSandboxBuilds = !!(
    gameDataResponse?.data?.isSandboxMode &&
    phase === "Builds" &&
    (gameDataResponse.data.currentOrders || []).some(
      (o) =>
        !o.toTerrID && (o.type === "Build Army" || o.type === "Build Fleet"),
    )
  );
  /*
   * 是否轮到我方推进：已点就绪，或本轮根本没有命令可下（orderStatus.None，
   * 服务端 Members::isReady() 同样把 None 视作已就绪）。两种情况都要走
   * 「倒计时 → 结算」这套表现。
   */
  // 「本轮确实没有命令可下」：不需要玩家做任何事
  const userNothingToDo =
    !!user?.member.orderStatus?.None && !pendingSandboxBuilds;
  const userDone = !!(user?.member.orderStatus?.Ready || userNothingToDo);

  const [botCountdown, setBotCountdown] = useState<number | null>(null);
  const [botResolveTimeout, setBotResolveTimeout] = useState(false);
  // 倒计时归零即进入「结算中」，保持轮询直到回合真的推进
  const botResolving = botCountdown === 0;

  /*
   * 就绪 / 无可下命令后开始倒计时；一旦该状态消失（回合已推进或玩家取消就绪）就复位。
   */
  useEffect(() => {
    if (!isBotGame || !userDone || gameIsFinished) {
      setBotCountdown(null);
      return undefined;
    }
    /*
     * 「本轮无需操作」（orderStatus.None，例如撤退阶段没有要撤的单位、建造阶段
     * 无处可建）：没有等玩家的必要，跳过倒计时直接进入结算轮询 —— 别让玩家
     * 以为要点什么、白等半天。
     */
    if (userNothingToDo) {
      setBotCountdown(0);
      return undefined;
    }
    setBotCountdown(BOT_RESOLVE_SECONDS);
    const timer = setInterval(() => {
      setBotCountdown((c) => (c === null ? null : Math.max(0, c - 1)));
    }, 1000);
    return () => clearInterval(timer);
  }, [isBotGame, userDone, userNothingToDo, gameIsFinished]);

  /*
   * 倒计时归零后主动拉取最新数据（overview + 棋盘），并每 1.5 秒重试一次，
   * 直到回合真的推进（userDone 变为 false 时上面的 effect 会停掉轮询）。
   */
  useEffect(() => {
    if (!botResolving) return undefined;
    const refresh = () => {
      const { game } = store.getState();
      if (!game.outstandingOverviewRequests) {
        dispatch(fetchGameOverview({ gameID: String(gameID) }));
      }
      dispatch(gameApiSliceActions.setNeedsGameData(true));
    };
    refresh();
    const timer = setInterval(refresh, BOT_POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [botResolving, dispatch, gameID]);

  /*
   * 结算长时间没推进就切换成提示文案：常见于早期创建、策略已卡死的旧局
   * （其它对局正常几秒内就会推进）。
   */
  useEffect(() => {
    if (!botResolving) {
      setBotResolveTimeout(false);
      return undefined;
    }
    const timer = setTimeout(
      () => setBotResolveTimeout(true),
      BOT_RESOLVE_TIMEOUT_MS,
    );
    return () => clearTimeout(timer);
  }, [botResolving]);

  let botProgressText: string | null = null;
  if (botCountdown !== null) {
    const userSc = user?.member.supplyCenterNo ?? 0;
    const userUnits = user?.member.unitNo ?? 0;
    /*
     * 只有「本轮确实没有建造订单(None)」才说明是无处可建。
     * 只要玩家能建造（有建造订单），就不该出现这个提示 —— 否则会误导。
     */
    if (
      phase === "Builds" &&
      userNothingToDo &&
      userSc > userUnits
    ) {
      botProgressText = t("No build site - your home supply centers are occupied");
    } else if (botResolveTimeout) {
      botProgressText = t("Still resolving - try refreshing or exit the game");
    } else if (userNothingToDo) {
      botProgressText = t("Nothing to do this phase - resolving automatically");
    } else if (botResolving) {
      botProgressText = t("Resolving turn...");
    } else {
      botProgressText = `${t("Next turn in")} ${botCountdown}s`;
    }
  }

  return (
    <div className="flex flex-col space-y-2">
      {user && (
        <div
          className={`py-1 px-2 rounded-md w-fit font-medium select-none text-white bg-${user.member.country.toLocaleLowerCase()}-main`}
        >
          {/* title={`Currently playing as ${user.member.country}`} */}
          {abbrMap[user.member.country]}
        </div>
      )}
      <WDPillScroller
        orderStatus={user?.member.orderStatus}
        country={user?.member.country || ""}
        viewedPhase={viewedPhase}
        viewedSeason={viewedSeason}
        viewedYear={viewedYear}
        orders={orders}
        phaseSelectorOpen={phaseSelectorOpen}
      />
      {botProgressText !== null && !gameIsFinished && (
        <div className="flex items-center py-1 px-3 rounded-md text-white bg-black bg-opacity-60 text-xs w-fit select-none">
          {botProgressText}
        </div>
      )}
      {(processTime || pauseTimeRemaining) && !gameIsFinished && !isBotGame && (
        <WDCountdownPill
          endTime={processTime || (pauseTimeRemaining || 0) + seconds}
          phaseTime={phaseSeconds}
          viewedPhase={viewedPhase}
          viewedSeason={viewedSeason}
          viewedYear={viewedYear}
          gamePhase={gamePhase}
          gameSeason={gameSeason}
          gameYear={gameYear}
          isPaused={processStatus === "Paused" || false}
        />
      )}
    </div>
  );
};

export default WDPhaseUI;
